import { useState, useCallback, useMemo, createContext, useContext } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { ImageViewer } from 'tdesign-react';

interface ImagePreviewContextValue {
  preview: (images: string | string[], index?: number) => void;
}

const ImagePreviewContext = createContext<ImagePreviewContextValue | null>(null);

/**
 * 在 ImagePreviewProvider 内使用，返回图片预览方法
 * preview(images) 支持字符串或字符串数组，index 指定初始展示的下标
 */
export function useImagePreview() {
  return useContext(ImagePreviewContext);
}

interface ImagePreviewProviderProps {
  children: ReactNode;
}

/**
 * 图片预览上下文：全局挂载一个 ImageViewer，
 * 供整个页面内的图片通过点击统一弹出大图预览
 */
export function ImagePreviewProvider({ children }: ImagePreviewProviderProps) {
  const [visible, setVisible] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [index, setIndex] = useState(0);

  const preview = useCallback((imgs: string | string[], idx: number = 0) => {
    const list = (Array.isArray(imgs) ? imgs : imgs ? [imgs] : [])
      .filter((item): item is string => Boolean(item));
    if (list.length === 0) return;
    setImages(list);
    setIndex(Math.min(Math.max(idx, 0), list.length - 1));
    setVisible(true);
  }, []);

  const handleClose = useCallback(() => setVisible(false), []);

  return (
    <ImagePreviewContext.Provider value={{ preview }}>
      {children}
      <ImageViewer
        visible={visible}
        images={images}
        index={index}
        onIndexChange={(val) => setIndex(val)}
        onClose={handleClose}
      />
    </ImagePreviewContext.Provider>
  );
}

interface PreviewableImageProps {
  /** 展示的图片地址；未传时取 images[index] */
  src?: string;
  /** 预览时可切换的图片列表，默认 [src] */
  images?: string[];
  /** 初始展示的下标，默认 0 */
  index?: number;
  alt?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
}

/**
 * 可点击预览的图片组件：
 * 在 ImagePreviewProvider 内点击图片会弹出大图预览（支持多图切换），
 * 未包裹 Provider 时等同于普通 img 标签
 */
export function PreviewableImage({
  src,
  images,
  index = 0,
  alt = '',
  className = '',
  style,
  onClick,
}: PreviewableImageProps) {
  const ctx = useImagePreview();

  const previewList = useMemo(() => {
    const list = images && images.length > 0 ? images : src ? [src] : [];
    return list.filter((item): item is string => Boolean(item));
  }, [src, images]);

  const displaySrc = src || previewList[index] || previewList[0];

  if (!displaySrc) return null;

  const handleClick = (e: MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) onClick(e);
    if (ctx && previewList.length > 0) {
      const initialIndex = src ? index : Math.min(index, previewList.length - 1);
      ctx.preview(previewList, initialIndex);
    }
  };

  return (
    <img
      src={displaySrc}
      alt={alt}
      style={style}
      className={`${ctx ? 'cursor-pointer hover:opacity-85 transition-opacity' : ''} ${className}`}
      onClick={handleClick}
    />
  );
}